import { useState } from "react";
import axios from "axios";
import BusyButton from "../components/basic/busy-button";
import ToggleSwitch from "../components/basic/toggle-switch";
import { apiHttpUrl } from "../helpers/api-base";
import { getAuthToken } from "../helpers/auth";

type OptionKey =
  "crop_width" | "crop_height" | "flip_horizontal" | "luminosity";

const OPTIONS: { key: OptionKey; label: string }[] = [
  { key: "crop_width", label: "Crop 2px width" },
  { key: "crop_height", label: "Crop 2px height" },
  { key: "flip_horizontal", label: "Flip horizontal" },
  { key: "luminosity", label: "Luminosity +0.5" },
];

interface MigrationAlterDialogProps {
  source: string;
  items: { group: string; name: string }[];
  onClose: () => void;
  /** Called after files changed on disk so previews can reload. */
  onAltered: () => void;
  setError?: (message: string) => void;
}

/** Popup: pick the edits, then apply them to every selected image/video. */
export default function MigrationAlterDialog({
  source,
  items,
  onClose,
  onAltered,
  setError,
}: MigrationAlterDialogProps) {
  const [options, setOptions] = useState<Record<OptionKey, boolean>>({
    crop_width: true,
    crop_height: true,
    flip_horizontal: true,
    luminosity: true,
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const anyOn = Object.values(options).some(Boolean);

  const apply = async () => {
    if (!anyOn || busy) return;
    setBusy(true);
    setMessage("");
    try {
      const token = getAuthToken();
      const res = await axios.post(
        apiHttpUrl("/api/migration/alter"),
        { source, items, options },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          validateStatus: () => true,
          timeout: 0,
        },
      );
      const data = res.data || {};
      if (data.error) setError?.(data.error);
      const altered: string[] = data.altered || [];
      const failed: string[] = data.failed || [];
      setMessage(
        `Altered ${altered.length} of ${items.length} file(s).` +
          (failed.length ? ` ${failed.length} failed: ${failed[0]}` : ""),
      );
      if (altered.length) onAltered();
    } catch {
      setError?.("Unable to reach the server.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="ib-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Alter media"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="ib-modal__panel">
        <h3 className="ib-modal__title">
          Alter {items.length} selected file(s)
        </h3>
        <p className="ib-migration-meta">
          Edits the downloaded files in place (images with sharp, videos with
          ffmpeg). Running it again applies the edits again.
        </p>
        <div className="ib-modal__options">
          {OPTIONS.map(({ key, label }) => (
            <div key={key} className="ib-modal__option">
              <span>{label}</span>
              <ToggleSwitch
                keyId={`alter-${key}`}
                checked={options[key]}
                ariaLabel={label}
                onChange={(e) =>
                  setOptions((p) => ({ ...p, [key]: e.target.checked }))
                }
              />
            </div>
          ))}
        </div>
        {busy && (
          <p className="ib-migration-meta">Working… videos can take a while.</p>
        )}
        {message && <p className="ib-migration-meta">{message}</p>}
        <div className="ib-migration-bar ib-modal__actions">
          <BusyButton
            variant="jf-btn jf-btn-primary ib-migration-btn"
            label="Apply"
            busy={busy}
            busyLabel="Altering…"
            disabled={!anyOn}
            onClick={apply}
          />
          <button
            type="button"
            className="jf-btn jf-btn-primary-outline ib-migration-btn"
            disabled={busy}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
